import { useSyncExternalStore } from "react";
import { isPhone } from "@/mobile/device";

// «Режим звонка» на телефоне — пока во вкладке идёт звонок (softphone.ts
// включает и выключает его по своему счётчику активных звонков).
//
// Как у обычного звонка из браузера не сделать: разговорный динамик у уха и
// датчик приближения странице недоступны. WebKit при захвате микрофона сам
// ставит аудиосессии PlayAndRecord в режиме VideoChat, а этот режим выводит
// звук на громкий динамик (или в наушники / AirPods, если подключены) — и
// тип, выставленный страницей, сводится к тому же (MediaSessionManagerCocoa).
// Что можно — делаем:
//  - не даём экрану погаснуть (Screen Wake Lock): погасший iPhone усыпляет
//    страницу, и микрофон пропадает посреди разговора;
//  - замечаем, что разговор прервал обычный звонок на телефон (состояние
//    аудиосессии «interrupted», Audio Session API iOS 17+), — окно звонка
//    говорит об этом.
//
// Тип аудиосессии НЕ выставляем. Было `play-and-record` (!288) — и в
// мобильной PWA пропал звук собеседника: явный тип меняет, куда iOS выводит
// звук (в разговорный динамик или никуда). Без него WebKit сам выбирает
// PlayAndRecord/VideoChat — громкий динамик, как и должно быть.

interface PageAudioSession {
  type: string;
  state?: string;
  addEventListener?: (type: "statechange", fn: () => void) => void;
  removeEventListener?: (type: "statechange", fn: () => void) => void;
}

interface ScreenLock {
  release: () => Promise<void>;
  addEventListener: (type: "release", fn: () => void) => void;
}

function audioSession(): PageAudioSession | null {
  return (navigator as Navigator & { audioSession?: PageAudioSession }).audioSession ?? null;
}

function wakeLock(): { request: (type: "screen") => Promise<ScreenLock> } | null {
  return (
    (navigator as Navigator & { wakeLock?: { request: (type: "screen") => Promise<ScreenLock> } })
      .wakeLock ?? null
  );
}

let on = false;
let lock: ScreenLock | null = null;
let interrupted = false;
const listeners = new Set<() => void>();

function setInterrupted(v: boolean) {
  if (interrupted === v) return;
  interrupted = v;
  listeners.forEach((l) => l());
}

async function acquireLock() {
  const wl = wakeLock();
  if (!on || lock || !wl || document.visibilityState !== "visible") return;
  try {
    const l = await wl.request("screen");
    // Пока ждали, звонок кончился — экран больше держать незачем.
    if (!on || lock) {
      void l.release().catch(() => undefined);
      return;
    }
    lock = l;
    // Браузер снимает блокировку сам, когда страница скрыта, — вернём её,
    // когда рекрутёр вернётся (onVisible).
    l.addEventListener("release", () => {
      if (lock === l) lock = null;
    });
  } catch {
    /* режим энергосбережения или запрет — звонок идёт и без этого */
  }
}

const onVisible = () => {
  if (document.visibilityState === "visible") void acquireLock();
};

const onSessionState = () => setInterrupted(audioSession()?.state === "interrupted");

/** Звонок начался. */
export function callModeOn(phone?: boolean) {
  if (on || typeof document === "undefined") return;
  if (!(phone ?? isPhone())) return;
  on = true;
  audioSession()?.addEventListener?.("statechange", onSessionState);
  document.addEventListener("visibilitychange", onVisible);
  void acquireLock();
}

/** Последний звонок вкладки кончился. */
export function callModeOff() {
  if (!on) return;
  on = false;
  audioSession()?.removeEventListener?.("statechange", onSessionState);
  document.removeEventListener("visibilitychange", onVisible);
  const l = lock;
  lock = null;
  void l?.release().catch(() => undefined);
  setInterrupted(false);
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

/** Разговор сейчас прерван обычным звонком на телефон (iOS). */
export function useCallInterrupted(): boolean {
  return useSyncExternalStore(subscribe, () => interrupted, () => false);
}

export const CALL_INTERRUPTED_TEXT =
  "Звук прерван: на телефоне идёт обычный звонок. Если после него собеседника не слышно — перезвоните.";
